import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import HeroSection from './components/HeroSection';
import PopularCalculators from './components/PopularCalculators';
import TaxComparisonTable from './components/TaxComparisonTable';
import HowItWorks from './components/HowItWorks';
import NoTaxStatesHighlight from './components/NoTaxStatesHighlight';
import BlogPreview from './components/BlogPreview';
import HomeFAQ from './components/HomeFAQ';
import HomeNewsletter from './components/HomeNewsletter';
import ForSaleHeroBanner from '../../components/feature/ForSaleHeroBanner';
import ForSaleFooterSection from '../../components/feature/ForSaleFooterSection';

const HomePage = () => {
  useDocumentMeta({
    title: 'Salary Tax Calculator 2026 - Free Take-Home Pay Calculator for 200+ US Cities',
    description:
      'Calculate your exact take-home pay with our free salary tax calculator. Accurate 2026 federal, state, and local tax breakdowns for 200+ US cities and the UK. Compare tax rates across states.',
    keywords:
      'salary calculator, tax calculator, take-home pay, net salary, paycheck calculator, income tax calculator, salary after tax 2026, state tax comparison',
    canonical: 'https://mysalarycalculator.net/',
    ogTitle: 'Salary Tax Calculator 2026 - Free Take-Home Pay Calculator',
    ogDescription:
      'Calculate your exact take-home pay with accurate federal, state, and local tax breakdowns for 200+ US cities. Free and updated for 2026.',
    ogUrl: 'https://mysalarycalculator.net/',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Salary Tax Calculator 2026 - Free Take-Home Pay Calculator',
    twitterDescription:
      'Calculate your exact take-home pay with accurate tax breakdowns for 200+ US cities.',
    metaTags: [
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Salary Tax Calculator' },
      { name: 'author', content: 'Salary Tax Calculator' },
      {
        name: 'last-modified',
        content: new Date().toISOString().split('T')[0],
      },
    ],
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': ['WebPage', 'FAQPage'],
        name: 'Salary Tax Calculator 2026',
        description:
          'Free salary tax calculator with accurate federal, state, and local tax breakdowns for 200+ US cities and the UK.',
        url: 'https://mysalarycalculator.net/',
        isPartOf: {
          '@type': 'WebSite',
          name: 'Salary Tax Calculator',
          url: 'https://mysalarycalculator.net/',
        },
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How accurate is this salary tax calculator?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our calculator uses the latest 2026 federal, state, and local tax brackets from official IRS and state revenue department sources. Estimates are within 1-2% of actual for most standard W-2 employees.',
            },
          },
          {
            '@type': 'Question',
            name: 'Which states have no income tax?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Nine US states have no state income tax: Alaska, Florida, Nevada, New Hampshire, South Dakota, Tennessee, Texas, Washington, and Wyoming.',
            },
          },
          {
            '@type': 'Question',
            name: 'What taxes are included in the take-home pay calculation?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We calculate federal income tax, state income tax, local/city income tax, Social Security tax (6.2%), and Medicare tax (1.45%). For UK calculations, we include Income Tax and National Insurance.',
            },
          },
          {
            '@type': 'Question',
            name: 'Can I compare take-home pay between different cities?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes! We cover 200+ US cities and UK locations. Each calculator automatically applies the correct federal, state, and local tax rates for accurate comparison.',
            },
          },
          {
            '@type': 'Question',
            name: 'How does filing status affect my take-home pay?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Filing status significantly impacts your tax bracket and standard deduction. Single filers get a $13,850 standard deduction, while married filing jointly get $27,700. Head of household filers get $20,800.',
            },
          },
          {
            '@type': 'Question',
            name: 'How often are the tax rates updated?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We update our tax rates annually when new federal and state tax brackets are released, typically in late October or November for the following tax year. Our current rates reflect the 2026 tax year.',
            },
          },
          {
            '@type': 'Question',
            name: 'Does the calculator account for pre-tax deductions like 401(k)?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, our advanced settings allow you to enter pre-tax deductions including 401(k) contributions, HSA contributions, and other pre-tax benefits. These deductions reduce your taxable income.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is this calculator suitable for freelancers and self-employed workers?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Our calculator is primarily designed for W-2 employees. Self-employed individuals face additional self-employment tax (15.3% on net earnings). You can use our calculator as a baseline and check our blog for detailed freelancer tax guides.',
            },
          },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-white">
      {/* For Sale Banner */}
      <ForSaleHeroBanner />

      {/* Header */}
      <header className="absolute top-10 left-0 right-0 z-50" role="banner">
        <nav
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5"
          aria-label="Main navigation"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <a href="/" aria-label="Salary Tax Calculator Home">
                <h1>
                  <img
                    src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                    alt="Salary Tax Calculator Logo - Free Take-Home Pay Calculator 2026"
                    className="h-10 w-auto cursor-pointer"
                    width="40"
                    height="40"
                  />
                </h1>
              </a>
              <span className="text-lg font-bold text-white hidden md:block">
                Salary Tax Calculator
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="/states"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors cursor-pointer hidden md:block"
              >
                US States
              </a>
              <a
                href="/blog"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors cursor-pointer hidden md:block"
              >
                Tax Tips Blog
              </a>
              <a
                href="/sitemap"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors cursor-pointer hidden md:block"
              >
                Sitemap
              </a>
              <a
                href="/usa/ny/manhattan"
                className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Try Calculator
              </a>
            </div>
          </div>
        </nav>
      </header>

      <main role="main">
        <HeroSection />
        <PopularCalculators />
        <NoTaxStatesHighlight />
        <TaxComparisonTable />
        <HowItWorks />
        <BlogPreview />
        <HomeFAQ />
        <HomeNewsletter />
      </main>

      {/* For Sale Footer Section */}
      <ForSaleFooterSection />

      {/* Footer */}
      <footer className="bg-teal-900 text-white py-16" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10"
            aria-label="Footer navigation"
          >
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f3cc483-3168-4437-a291-d29def05c8fe.png"
                  alt="Salary Tax Calculator"
                  className="h-9 w-auto"
                  width="36"
                  height="36"
                />
                <span className="text-lg font-bold">Salary Tax Calculator</span>
              </div>
              <p className="text-teal-200 text-sm leading-relaxed max-w-sm mb-4">
                The most accurate free salary and tax calculator for US and UK
                locations. Calculate your take-home pay with precise federal,
                state, and local tax breakdowns. Updated for 2026.
              </p>
              <p className="text-teal-300 text-xs">
                <strong>Last updated:</strong>{' '}
                {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-teal-100">
                <a href="/states" className="hover:text-white transition-colors cursor-pointer">
                  Popular States
                </a>
              </h4>
              <ul className="space-y-2.5 text-sm text-teal-300">
                <li>
                  <a href="/states/ca" className="hover:text-white transition-colors cursor-pointer">
                    California
                  </a>
                </li>
                <li>
                  <a href="/states/tx" className="hover:text-white transition-colors cursor-pointer">
                    Texas
                  </a>
                </li>
                <li>
                  <a href="/states/ny" className="hover:text-white transition-colors cursor-pointer">
                    New York
                  </a>
                </li>
                <li>
                  <a href="/states/fl" className="hover:text-white transition-colors cursor-pointer">
                    Florida
                  </a>
                </li>
                <li>
                  <a href="/states/wa" className="hover:text-white transition-colors cursor-pointer">
                    Washington
                  </a>
                </li>
                <li>
                  <a href="/states/il" className="hover:text-white transition-colors cursor-pointer">
                    Illinois
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-teal-100">
                <a href="/blog" className="hover:text-white transition-colors cursor-pointer">
                  Tax Guides
                </a>
              </h4>
              <ul className="space-y-2.5 text-sm text-teal-300">
                <li>
                  <a
                    href="/blog/manhattan-freelancer-taxes-2026"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    NYC Freelancer Taxes
                  </a>
                </li>
                <li>
                  <a
                    href="/blog/austin-no-state-tax-guide"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Austin Zero Tax Guide
                  </a>
                </li>
                <li>
                  <a
                    href="/blog/san-francisco-tech-taxes"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    SF Tech Tax Guide
                  </a>
                </li>
                <li>
                  <a
                    href="/blog/manhattan-remote-work-taxes"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Remote Work Taxes
                  </a>
                </li>
                <li>
                  <a
                    href="/blog/london-uk-tax-guide"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    London UK Tax Guide
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-teal-100">Resources</h4>
              <ul className="space-y-2.5 text-sm text-teal-300">
                <li>
                  <a href="/usa/ny/manhattan" className="hover:text-white transition-colors cursor-pointer">
                    Manhattan Calculator
                  </a>
                </li>
                <li>
                  <a href="/usa/ca/san-francisco" className="hover:text-white transition-colors cursor-pointer">
                    SF Calculator
                  </a>
                </li>
                <li>
                  <a href="/uk/london" className="hover:text-white transition-colors cursor-pointer">
                    London Calculator
                  </a>
                </li>
                <li>
                  <a href="/sitemap" className="hover:text-white transition-colors cursor-pointer">
                    Sitemap
                  </a>
                </li>
                <li>
                  <a href="/privacy" className="hover:text-white transition-colors cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-white transition-colors cursor-pointer">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </nav>

          <div className="border-t border-teal-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-teal-400">
            <p>&copy; 2026 Salary Tax Calculator. All rights reserved.</p>
            <p>
              <a
                href="https://cyberneticgenius.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
